export type CrmPrintOrientation = 'portrait' | 'landscape';

export interface CrmPrintReportConfig {
  title: string;
  subtitle?: string;
  meta?: string[];
  orientation?: CrmPrintOrientation;
  sections: string[];
}

export interface CrmPrintMetric {
  label: string;
  value: string | number;
  note?: string;
}

export interface CrmPrintField {
  label: string;
  value: unknown;
  fullWidth?: boolean;
}

export interface CrmPrintFieldCard {
  title: string;
  subtitle?: string;
  meta?: string[];
  fields: CrmPrintField[];
}

export interface CrmPrintTableColumn {
  label: string;
  width?: string;
  nowrap?: boolean;
  align?: 'left' | 'center' | 'right';
}

export type CrmPrintTableCellValue = unknown | unknown[];

const CRM_PRINT_HOST_CLASS = 'crm-print-host';
const CRM_PRINT_ACTIVE_CLASS = 'crm-print-active';
const NO_BREAK_STYLE = 'page-break-inside: avoid; break-inside: avoid;';
const SECTION_BASE_STYLE = 'margin: 0 0 10px; padding: 8px 0 0; border-top: 1px solid #cbd5e1; background: #ffffff;';

export function printCrmReport(config: CrmPrintReportConfig): void {
  if (typeof document === 'undefined' || typeof window === 'undefined' || !document.body) {
    return;
  }

  cleanupExistingPrintHost();

  const originalTitle = document.title;
  const host = document.createElement('div');
  host.className = CRM_PRINT_HOST_CLASS;
  host.innerHTML = `
    <style>
      ${getCrmPrintStyles(config.orientation || 'portrait')}
    </style>
    <article class="crm-print-report">
      <header class="crm-print-report-header">
        <div class="crm-print-report-kicker">CRM Printable Report</div>
        <h1>${escapeHtml(config.title)}</h1>
        ${config.subtitle ? `<p class="crm-print-report-subtitle">${escapeHtml(config.subtitle)}</p>` : ''}
        <div class="crm-print-report-meta">
          ${[
            `Generated ${escapeHtml(formatPrintDate(new Date().toISOString(), true))}`,
            ...(config.meta || []).map((item) => escapeHtml(item))
          ].map((item) => `<span class="crm-print-report-meta-item">${item}</span>`).join('')}
        </div>
      </header>
      ${config.sections.join('')}
    </article>
  `;

  const cleanup = () => {
    document.body.classList.remove(CRM_PRINT_ACTIVE_CLASS);
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
    document.title = originalTitle;
    window.removeEventListener('afterprint', cleanup);
  };

  document.body.appendChild(host);
  document.body.classList.add(CRM_PRINT_ACTIVE_CLASS);
  document.title = config.title;
  window.addEventListener('afterprint', cleanup, { once: true });

  waitForImages(host).then(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  });
}

export function renderPrintSection(
  title: string,
  content: string,
  description?: string,
  allowPageBreaks: boolean = false
): string {
  return `
    <section class="crm-print-section" style="${getSectionStyle(allowPageBreaks)}">
      <div class="crm-print-section-header">
        <h2>${escapeHtml(title)}</h2>
        ${description ? `<p>${escapeHtml(description)}</p>` : ''}
      </div>
      <div class="crm-print-section-content">
        ${content}
      </div>
    </section>
  `;
}

export function renderMetricGrid(metrics: CrmPrintMetric[]): string {
  if (!metrics.length) {
    return renderEmptyState('No metrics are available for this report.');
  }

  return renderTable(
    [
      { label: 'Metric', width: '34%' },
      { label: 'Value', width: '22%', nowrap: true },
      { label: 'Note', width: '44%' }
    ],
    metrics.map((metric) => [
      metric.label,
      metric.value,
      metric.note || '-'
    ]),
    'No metrics are available for this report.'
  );
}

export function renderFieldGrid(fields: CrmPrintField[]): string {
  if (!fields.length) {
    return renderEmptyState('No details are available for this section.');
  }

  return `
    <div class="crm-print-table-wrap">
      <table class="crm-print-table crm-print-table-key-value">
        <tbody>
          ${fields.map((field) => `
            <tr style="${NO_BREAK_STYLE}">
              <th style="${getKeyValueLabelStyle()}">
                ${escapeHtml(field.label)}
              </th>
              <td style="${getKeyValueValueStyle()}">
                ${renderValueLines(field.value)}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

export function renderFieldCards(cards: CrmPrintFieldCard[], emptyMessage: string): string {
  if (!cards.length) {
    return renderEmptyState(emptyMessage);
  }

  return `
    <div class="crm-print-record-list">
      ${cards.map((card) => `
        <article class="crm-print-record-table">
          <div class="crm-print-card-header">
            <div>
              <h3>${escapeHtml(card.title)}</h3>
              ${card.subtitle ? `<p>${escapeHtml(card.subtitle)}</p>` : ''}
            </div>
          </div>
          ${card.meta && card.meta.length > 0 ? `
            <div class="crm-print-card-meta">
              ${card.meta.map((item) => `<span class="crm-print-card-meta-item">${escapeHtml(item)}</span>`).join('')}
            </div>
          ` : ''}
          <div class="crm-print-table-wrap">
            <table class="crm-print-table crm-print-table-key-value">
              <tbody>
                ${card.fields.map((field) => `
                  <tr style="${NO_BREAK_STYLE}">
                    <th style="${getKeyValueLabelStyle()}">
                      ${escapeHtml(field.label)}
                    </th>
                    <td style="${getKeyValueValueStyle()}">
                      ${renderValueLines(field.value)}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

export function renderTable(
  columns: CrmPrintTableColumn[],
  rows: CrmPrintTableCellValue[][],
  emptyMessage: string
): string {
  if (!rows.length) {
    return renderEmptyState(emptyMessage);
  }

  const resolvedColumns = withResolvedColumnWidths(columns);

  return `
    <div class="crm-print-table-wrap">
      <table class="crm-print-table">
        <colgroup>
          ${resolvedColumns.map((column) => `<col style="width: ${column.width}">`).join('')}
        </colgroup>
        <thead>
          <tr>
            ${resolvedColumns.map((column) => `
              <th style="${getTableCellStyle(column, true)}">
                ${escapeHtml(column.label)}
              </th>
            `).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows.map((row) => `
            <tr style="${NO_BREAK_STYLE}">
              ${resolvedColumns.map((column, index) => `
                <td style="${getTableCellStyle(column, false)}">
                  ${renderValueLines(row[index])}
                </td>
              `).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

export function renderEmptyState(message: string): string {
  return `<div class="crm-print-empty">${escapeHtml(message)}</div>`;
}

export function formatPrintDate(value: string | null | undefined, includeTime: boolean = false): string {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('en-US', includeTime
    ? {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }
    : {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(parsedDate);
}

export function formatPrintValue(value: unknown, fallback: string = '-'): string {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  if (Array.isArray(value)) {
    const formattedItems = value
      .map((item) => formatPrintValue(item, ''))
      .filter((item) => item.length > 0);

    return formattedItems.length > 0 ? formattedItems.join(', ') : fallback;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function cleanupExistingPrintHost(): void {
  const existingHost = document.querySelector(`.${CRM_PRINT_HOST_CLASS}`);
  if (existingHost && existingHost.parentNode) {
    existingHost.parentNode.removeChild(existingHost);
  }

  if (document.body) {
    document.body.classList.remove(CRM_PRINT_ACTIVE_CLASS);
  }
}

function waitForImages(host: HTMLElement): Promise<void> {
  const images = Array.from(host.querySelectorAll('img'));

  if (!images.length) {
    return Promise.resolve();
  }

  return Promise.all(images.map((image) => {
    if ((image as HTMLImageElement).complete) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    });
  })).then(() => undefined);
}

function renderValueLines(value: unknown): string {
  const lines = toPrintLines(value);

  return lines.map((line) => `<div class="crm-print-line">${escapeHtml(line)}</div>`).join('');
}

function toPrintLines(value: unknown): string[] {
  if (Array.isArray(value)) {
    const arrayLines = value.reduce((lines, item) => lines.concat(toPrintLines(item)), [] as string[]);
    return arrayLines.length > 0 ? arrayLines : ['-'];
  }

  const formattedValue = formatPrintValue(value);
  const splitLines = formattedValue
    .split(/\r?\n/g)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return splitLines.length > 0 ? splitLines : [formattedValue];
}

function getSectionStyle(allowPageBreaks: boolean): string {
  return [
    allowPageBreaks ? 'page-break-inside: auto; break-inside: auto;' : NO_BREAK_STYLE,
    SECTION_BASE_STYLE
  ].join(' ');
}

function withResolvedColumnWidths(columns: CrmPrintTableColumn[]): CrmPrintTableColumn[] {
  const defaultWidth = `${(100 / Math.max(columns.length, 1)).toFixed(2)}%`;
  const columnsMissingWidth = columns.filter((column) => !column.width);
  const specifiedPercentTotal = columns.reduce((total, column) => {
    const percentWidth = parsePercentWidth(column.width);
    return percentWidth === null ? total : total + percentWidth;
  }, 0);

  const fallbackPercentWidth = columnsMissingWidth.length > 0
    ? `${Math.max(0, 100 - specifiedPercentTotal) / columnsMissingWidth.length}%`
    : defaultWidth;

  return columns.map((column) => ({
    ...column,
    width: column.width || fallbackPercentWidth || defaultWidth
  }));
}

function parsePercentWidth(width: string | undefined): number | null {
  if (!width) {
    return null;
  }

  const match = width.trim().match(/^([0-9]+(?:\.[0-9]+)?)%$/);
  if (!match) {
    return null;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
}

function getTableCellStyle(column: CrmPrintTableColumn, isHeader: boolean): string {
  return [
    'border: 1px solid #d1d5db',
    isHeader ? 'background: #f8fafc' : 'background: #ffffff',
    'padding: 5px 7px',
    'vertical-align: top',
    'font-size: 11px',
    'line-height: 1.3',
    `text-align: ${column.align || 'left'}`,
    column.width ? `width: ${column.width}` : '',
    'white-space: normal',
    column.nowrap ? '' : 'overflow-wrap: anywhere',
    column.nowrap ? '' : 'word-break: break-word',
    NO_BREAK_STYLE
  ].filter((item) => item.length > 0).join('; ');
}

function getKeyValueLabelStyle(): string {
  return [
    'border: 1px solid #d1d5db',
    'border-right: 0',
    'background: #fafafa',
    'padding: 4px 7px',
    'vertical-align: top',
    'text-align: left',
    'font-size: 10px',
    'line-height: 1.25',
    'font-weight: 700',
    'letter-spacing: 0.03em',
    'text-transform: uppercase',
    'color: #334155',
    'width: 180px',
    NO_BREAK_STYLE
  ].join('; ');
}

function getKeyValueValueStyle(): string {
  return [
    'border: 1px solid #d1d5db',
    'padding: 4px 7px',
    'vertical-align: top',
    'font-size: 11px',
    'line-height: 1.3',
    'color: #111827',
    NO_BREAK_STYLE
  ].join('; ');
}

function getCrmPrintStyles(orientation: CrmPrintOrientation): string {
  return `
    @page {
      size: ${orientation};
      margin: 10mm 9mm;
    }

    body.${CRM_PRINT_ACTIVE_CLASS} {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
    }

    body.${CRM_PRINT_ACTIVE_CLASS} > * {
      display: none !important;
    }

    body.${CRM_PRINT_ACTIVE_CLASS} > .${CRM_PRINT_HOST_CLASS} {
      display: block !important;
      position: fixed;
      inset: 0;
      overflow: auto;
      background: #ffffff;
      z-index: 2147483647;
    }

    .${CRM_PRINT_HOST_CLASS} {
      display: none;
    }

    .crm-print-report {
      max-width: 1120px;
      margin: 0 auto;
      padding: 14px 18px;
      color: #111827;
      font-family: "Segoe UI", Arial, Helvetica, sans-serif;
      font-size: 11px;
      line-height: 1.3;
      box-sizing: border-box;
    }

    .crm-print-report-header {
      ${NO_BREAK_STYLE}
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #64748b;
    }

    .crm-print-report-kicker {
      color: #0f4c81;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .crm-print-report-header h1,
    .crm-print-section-header h2,
    .crm-print-card-header h3 {
      margin: 0;
      color: #0f172a;
    }

    .crm-print-report-header h1 {
      margin-top: 3px;
      font-size: 21px;
      line-height: 1.15;
    }

    .crm-print-section-header h2 {
      font-size: 14px;
      line-height: 1.2;
    }

    .crm-print-card-header h3 {
      font-size: 13px;
      line-height: 1.2;
    }

    .crm-print-report-subtitle,
    .crm-print-section-header p,
    .crm-print-card-header p {
      margin: 4px 0 0;
      color: #475569;
    }

    .crm-print-report-meta,
    .crm-print-card-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 6px;
    }

    .crm-print-report-meta-item,
    .crm-print-card-meta-item {
      ${NO_BREAK_STYLE}
      display: inline;
      padding: 0;
      color: #475569;
      font-size: 10px;
      font-weight: 600;
    }

    .crm-print-report-meta-item:not(:last-child)::after,
    .crm-print-card-meta-item:not(:last-child)::after {
      content: " | ";
      color: #94a3b8;
    }

    .crm-print-section-header {
      ${NO_BREAK_STYLE}
      margin-bottom: 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid #e5e7eb;
    }

    .crm-print-record-list {
      display: grid;
      gap: 8px;
    }

    .crm-print-record-table {
      ${NO_BREAK_STYLE}
      padding: 0;
    }

    .crm-print-card-header {
      ${NO_BREAK_STYLE}
      display: block;
      margin-bottom: 4px;
    }

    .crm-print-label {
      color: #475569;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .crm-print-value-block,
    .crm-print-line {
      color: #111827;
    }

    .crm-print-line + .crm-print-line {
      margin-top: 2px;
    }

    .crm-print-table-wrap {
      width: 100%;
      overflow: hidden;
    }

    .crm-print-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    .crm-print-table-key-value {
      table-layout: auto;
    }

    .crm-print-table thead {
      display: table-header-group;
    }

    .crm-print-table tr {
      ${NO_BREAK_STYLE}
    }

    .crm-print-empty {
      ${NO_BREAK_STYLE}
      padding: 6px 0;
      color: #64748b;
      font-style: italic;
    }

    .crm-print-logo-frame {
      ${NO_BREAK_STYLE}
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 90px;
      padding: 8px;
      border: 1px solid #d1d5db;
      background: #ffffff;
    }

    .crm-print-logo-frame img {
      max-width: 100%;
      max-height: 72px;
      object-fit: contain;
    }

    @media print {
      body.${CRM_PRINT_ACTIVE_CLASS} > .${CRM_PRINT_HOST_CLASS} {
        position: static !important;
        overflow: visible !important;
      }

      .crm-print-report {
        max-width: none;
        padding: 0;
      }
    }
  `;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
